// Shared design tokens + style objects for the whole Subscription feature
// (Plans / Manage License / Accounts). Imported by PlansPage.js,
// LicensePage.js and AccountsPage.js.
export const C = {
  primary: "#2563eb",
  primaryBg: "#eff6ff",
  text: "#111827",
  muted: "#6b7280",
  border: "#e5e7eb",
  bg: "#f9fafb",
  success: "#10b981",
  successBg: "#ecfdf5",
  warning: "#d97706",
  warningBg: "#fffbeb",
};

export const S = {
  page: {
    color: C.text,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Helvetica, Arial, sans-serif',
  },
  container: { maxWidth: 1100, margin: "0 auto" },
  titleBlock: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  title: { fontSize: 28, fontWeight: 800, margin: "0 0 6px" },
  subtitle: { color: C.muted, fontSize: 14.5, margin: 0 },

  accountBadge: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 999,
    padding: "6px 8px",
  },
  accountBadgeAvatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: C.primaryBg,
    color: C.primary,
    fontWeight: 800,
    fontSize: 13,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  accountBadgeInfo: { display: "flex", flexDirection: "column", lineHeight: 1.3 },
  accountBadgeCompany: { fontSize: 13, fontWeight: 700 },
  accountBadgeUser: { fontSize: 11.5, color: C.muted },
  accountBadgeEditBtn: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "none",
    background: C.primaryBg,
    color: C.primary,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12.5,
    flexShrink: 0,
  },

  tabs: {
    display: "flex",
    gap: 8,
    borderBottom: `1px solid ${C.border}`,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 16px",
    fontWeight: 600,
    fontSize: 14,
    color: C.muted,
    border: "none",
    background: "transparent",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    marginBottom: -1,
  },
  tabActive: { color: C.primary, borderBottomColor: C.primary },

  plansGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 20,
    maxWidth: 780,
    margin: "0 auto",
    padding: "8px 2px 2px",
    alignItems: "start",
  },
  planCard: {
    position: "relative",
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: "20px 22px 18px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },
  planCardPopular: {
    borderColor: C.primary,
    borderWidth: 2,
    boxShadow: "0 12px 24px -10px rgba(37, 99, 235, 0.3)",
    transform: "translateY(-3px)",
  },
  planCardSelected: {
    border: `1.5px solid ${C.primary}`,
    boxShadow: "0 8px 24px rgba(37, 99, 235, 0.12)",
  },
  popularBadge: {
    position: "absolute",
    top: -11,
    left: "50%",
    transform: "translateX(-50%)",
    background: C.primary,
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    padding: "3px 12px",
    borderRadius: 999,
    boxShadow: "0 6px 14px rgba(37, 99, 235, 0.35)",
    whiteSpace: "nowrap",
  },
  planTitle: { fontSize: 17, fontWeight: 800, margin: "0 0 4px" },
  planDesc: {
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.4,
    margin: "0 0 12px",
  },
  divider: { borderTop: `1px solid ${C.border}`, marginBottom: 12 },
  price: { fontSize: 34, fontWeight: 800, lineHeight: 1 },
  priceRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "center",
    gap: 5,
    flexWrap: "wrap",
  },
  priceCurrency: { fontSize: 12, fontWeight: 700, color: C.muted },
  priceAmount: { fontSize: 26, fontWeight: 800, lineHeight: 1, color: C.text },
  priceUnit: { fontSize: 11, fontWeight: 600, color: C.muted },
  priceLine: { fontSize: 20, fontWeight: 800, lineHeight: 1.3 },
  pricePeriod: { color: C.muted, fontSize: 10.5, marginTop: 2, marginBottom: 12 },
  featureList: {
    listStyle: "none",
    margin: "0 0 14px",
    padding: 0,
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    flex: 1,
  },
  featureItem: { display: "flex", alignItems: "center", gap: 9, fontSize: 12 },
  featureIconWrap: {
    width: 15,
    height: 15,
    borderRadius: "50%",
    background: C.successBg,
    color: C.success,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  featureIcon: { color: C.primary, marginTop: 3, fontSize: 12, flexShrink: 0 },

  btn: {
    border: "none",
    borderRadius: 10,
    padding: "9px 16px",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    width: "100%",
  },
  btnAuto: { width: "auto" },
  btnPrimary: { background: C.primary, color: "#fff", boxShadow: "0 8px 16px -4px rgba(37, 99, 235, 0.45)" },
  btnOutline: { background: "#fff", color: C.primary, border: `1.5px solid ${C.primary}` },
  btnCurrent: { background: "#f3f4f6", color: C.muted, cursor: "default" },
  btnSm: { padding: "7px 14px", fontSize: 12.5 },

  footnote: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 14,
    fontSize: 12.5,
    color: C.text,
    flexWrap: "wrap",
    textAlign: "center",
  },
  footnoteIcon: {
    color: C.primary,
    background: C.primaryBg,
    width: 22,
    height: 22,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    flexShrink: 0,
  },
  subfootnote: { textAlign: "center", color: C.muted, fontSize: 12.5, marginTop: 8 },

  panel: {
    maxWidth: 820,
    margin: "0 auto",
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: 28,
  },
  panelWide: {
    maxWidth: "100%",
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: 28,
  },
  panelHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
    marginBottom: 22,
  },
  panelTitle: { fontSize: 17, fontWeight: 800, margin: "0 0 4px" },
  panelDesc: { color: C.muted, fontSize: 13, margin: 0 },

  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "none",
    background: "transparent",
    color: C.muted,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: 20,
    padding: 0,
  },
  checkoutGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: 24,
    marginBottom: 20,
  },
  planRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0",
    borderBottom: `1px solid ${C.border}`,
    fontSize: 13.5,
    gap: 12,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 8,
  },
  termsBox: {
    background: C.primaryBg,
    border: "1px solid #dbeafe",
    borderRadius: 12,
    padding: 18,
    marginTop: 4,
  },
  tagPromo: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: C.successBg,
    color: C.success,
    padding: "6px 10px",
    borderRadius: 8,
    fontSize: 12.5,
    fontWeight: 700,
  },
  tagRemove: {
    border: "none",
    background: "transparent",
    color: "inherit",
    cursor: "pointer",
    fontSize: 14,
    lineHeight: 1,
    padding: 0,
  },
  formGroup: { marginBottom: 14 },
  formLabel: { display: "block", fontSize: 12, fontWeight: 700, marginBottom: 6 },
  formInput: {
    width: "100%",
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: "9px 12px",
    fontSize: 13.5,
    outline: "none",
  },
  formInputError: {
    borderColor: "#dc2626",
  },
  fieldErrorText: {
    color: "#dc2626",
    fontSize: 12,
    margin: "5px 0 0",
  },

  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: 11.5,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: C.muted,
    padding: "0 10px 10px 0",
    borderBottom: `1px solid ${C.border}`,
  },
  td: {
    padding: "14px 10px 14px 0",
    borderBottom: `1px solid ${C.border}`,
    fontSize: 13.5,
    verticalAlign: "middle",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 10px",
    borderRadius: 999,
  },
  badgeSuccess: { background: C.successBg, color: C.success },
  badgeWarning: { background: C.warningBg, color: C.warning },
  badgeMuted: { background: "#f3f4f6", color: C.muted },
  badgePrimary: { background: C.primaryBg, color: C.primary },

  licenseCard: {
    display: "inline-flex",
    alignItems: "center",
    gap: 14,
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 999,
    padding: "8px 16px",
    marginBottom: 18,
    flexWrap: "wrap",
  },
  licenseStat: { display: "flex", alignItems: "baseline", gap: 5 },
  licenseStatNum: { fontSize: 15, fontWeight: 800, lineHeight: 1 },
  licenseStatLabel: { fontSize: 11.5, color: C.muted, fontWeight: 600 },
  licenseDivider: { width: 1, height: 16, background: C.border },

  usageTrack: {
    width: "100%",
    height: 8,
    borderRadius: 999,
    background: "#f3f4f6",
    overflow: "hidden",
    marginTop: 8,
  },
  usageFill: {
    height: "100%",
    borderRadius: 999,
    background: C.primary,
    transition: "width 0.3s ease",
  },
  usageFillFull: { background: C.warning },
  stepperInput: {
    width: 90,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: "9px 12px",
    fontSize: 13.5,
    outline: "none",
  },

  planHero: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderLeft: `4px solid ${C.primary}`,
    borderRadius: 16,
    padding: "22px 28px",
    marginBottom: 22,
  },
  planHeroLabel: {
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: C.muted,
    margin: "0 0 4px",
  },
  planHeroName: { fontSize: 24, fontWeight: 800, margin: 0, color: C.text },
  planHeroRate: { textAlign: "right" },
  planHeroPrice: { fontSize: 30, fontWeight: 800, lineHeight: 1, color: C.primary },
  planHeroPricePeriod: { fontSize: 12.5, color: C.muted, marginTop: 4 },

  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 18,
  },
  detailTile: {
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: "14px 16px",
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: C.muted,
    marginBottom: 6,
  },
  detailValue: { fontSize: 15, fontWeight: 700 },

  usageStatsRow: { display: "flex", gap: 32, flexWrap: "wrap", marginBottom: 4 },
  usageStatNum: { fontSize: 26, fontWeight: 800, lineHeight: 1 },
  usageStatLabel: { fontSize: 11.5, color: C.muted, fontWeight: 600, marginTop: 4 },

  rowActionsCell: { display: "flex", alignItems: "center", gap: 16 },
  rowEditIconBtn: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    border: "none",
    background: C.primaryBg,
    color: C.primary,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
  },
  rowSwitchIconBtn: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    border: "none",
    background: "#f3f4f6",
    color: C.muted,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
  },

  tableActionBtn: {
    border: "none",
    background: "transparent",
    color: C.primary,
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 700,
    marginRight: 16,
    padding: 0,
  },
  tableActionBtnMuted: {
    border: "none",
    background: "transparent",
    color: C.muted,
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 700,
    padding: 0,
  },

  sectionHeading: { fontSize: 14, fontWeight: 700, margin: "0 0 4px" },
  sectionSubheading: { fontSize: 12, color: C.muted, margin: "0 0 12px" },

  agreementBody: {
    fontSize: 12.5,
    lineHeight: 1.6,
    color: C.text,
  },
  agreementClamped: {
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  agreementPara: { margin: "0 0 12px" },
  agreementSectionTitle: { fontWeight: 800, fontSize: 13, margin: "18px 0 8px" },
  agreementLine: { margin: "0 0 6px" },
  agreementBlank: { margin: "0 0 6px", color: C.muted },
  agreementToggleBtn: {
    border: "none",
    background: "transparent",
    color: C.primary,
    fontWeight: 700,
    fontSize: 12.5,
    cursor: "pointer",
    padding: 0,
    marginTop: 10,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  agreementScroll: {
    maxHeight: 360,
    overflowY: "auto",
    paddingRight: 6,
  },

  expandBtn: {
    border: "none",
    background: "#f3f4f6",
    color: C.muted,
    cursor: "pointer",
    width: 24,
    height: 24,
    borderRadius: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    marginRight: 8,
    transition: "transform 0.15s ease",
  },

  detailRow: { background: "#fafafa" },
  detailRowCell: { padding: "0", borderBottom: `1px solid ${C.border}` },
  detailRowInner: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 18,
    padding: "16px 20px",
  },
  detailRowLabel: {
    fontSize: 10.5,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: C.muted,
    fontWeight: 700,
    marginBottom: 4,
  },
  detailRowValue: { fontSize: 13, fontWeight: 600 },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    background: "#fff",
    borderRadius: 16,
    width: "100%",
    maxWidth: 460,
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 24px 60px rgba(0, 0, 0, 0.25)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 20px",
    borderBottom: `1px solid ${C.border}`,
  },
  modalTitle: { fontSize: 15, fontWeight: 800, margin: 0 },
  modalClose: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: "none",
    background: C.bg,
    color: C.muted,
    cursor: "pointer",
  },
  modalBody: { padding: "18px 20px" },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "14px 20px 18px",
  },
};
